/**
 * RequestCard Component
 * Card displaying a processing request with hover overlay for parameters
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, CheckCircle, Loader2, AlertCircle, Plus, Info, X } from 'lucide-react';
import { GlassCard, GlassCardContent } from '@/components/common/GlassCard';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { cn } from '@/lib/utils';
import type { ProcessingRequest } from '@/types/api';
import { ProcessingStatus } from '@/types/api';

export interface RequestCardProps {
  /** Processing request data (null for empty card) */
  request: ProcessingRequest | null;
  /** Additional class names */
  className?: string;
}

/**
 * Format date for display as dd.mm.yyyy hh:mm
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
}

/**
 * Get status badge config based on numeric status
 */
function getStatusConfig(status: ProcessingStatus, t: (key: string) => string) {
  switch (status) {
    case ProcessingStatus.Completed:
      return {
        label: t('requestCard.statusCompleted'),
        icon: CheckCircle,
        className: 'bg-green-100 text-green-700',
      };
    case ProcessingStatus.InProgress:
      return {
        label: t('requestCard.statusInProgress'),
        icon: Loader2,
        className: 'bg-blue-100 text-blue-700',
        animate: true,
      };
    case ProcessingStatus.Pending:
      return {
        label: t('requestCard.statusPending'),
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-700',
      };
    case ProcessingStatus.Failed:
      return {
        label: t('requestCard.statusFailed'),
        icon: AlertCircle,
        className: 'bg-red-100 text-red-700',
      };
    default:
      return {
        label: t('requestCard.statusPending'),
        icon: Clock,
        className: 'bg-gray-100 text-gray-700',
      };
  }
}

/**
 * Empty card component for "Create Analysis" placeholder
 */
function EmptyRequestCard({ className }: { className?: string }) {
  const { t } = useTranslation();
  const openAnalysisDialog = useAnalysisDialogStore((s) => s.open);

  return (
    <button onClick={openAnalysisDialog} data-track="request_card_create_analysis" className="block w-full text-left cursor-pointer">
      <GlassCard
        interactive
        className={cn(
          'cursor-pointer h-full min-h-[140px] border-dashed border-2 border-gray-300 flex flex-col',
          className
        )}
      >
        <GlassCardContent className="pt-6 flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[var(--color-primary-start)] to-[var(--color-primary-end)] flex items-center justify-center mb-3">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm font-medium text-[var(--color-charcoal)]/70">
            {t('requestCard.createAnalysis')}
          </span>
        </GlassCardContent>
      </GlassCard>
    </button>
  );
}

/**
 * Request card component for dashboard
 */
export function RequestCard({ request, className }: RequestCardProps) {
  const { t } = useTranslation();
  const [showOverlay, setShowOverlay] = useState(false);

  // Return empty card if no request
  if (!request) {
    return <EmptyRequestCard className={className} />;
  }

  const statusConfig = getStatusConfig(request.status, t);
  const StatusIcon = statusConfig.icon;
  const hasParameters = request.payloadParameters.length > 0;

  const handleInfoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowOverlay(!showOverlay);
  };

  const handleCloseOverlay = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowOverlay(false);
  };

  return (
    <div className="relative">
      {/* Parameters overlay - positioned above the card */}
      {showOverlay && hasParameters && (
        <div
          className={cn(
            'absolute left-0 right-0 bottom-full mb-2 z-50',
            'bg-white/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200',
            'p-4 animate-in fade-in slide-in-from-bottom-2 duration-200'
          )}
        >
          <div className="flex items-start justify-between gap-2 mb-3">
            <h4 className="text-sm font-semibold text-[var(--color-charcoal)]">
              {t('requestCard.analysisParameters')}
            </h4>
            {/* Close button - visible on mobile/tablet */}
            <button
              onClick={handleCloseOverlay}
              className="md:hidden p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4 text-[var(--color-charcoal)]/60" />
            </button>
          </div>
          <div className="space-y-2">
            {request.payloadParameters.map((param, index) => (
              <div key={index} className="flex items-start gap-2 text-sm">
                <span className="font-medium text-[var(--color-charcoal)]/70 min-w-[100px]">
                  {param.parameterName}:
                </span>
                <span className="text-[var(--color-charcoal)]">
                  {param.payload}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to={`/analysis/${request.correlationId}`} data-track="request_card_view_analysis" className="block">
        <GlassCard
          interactive
          className={cn('cursor-pointer min-h-[140px]', className)}
          role="article"
        >
          <GlassCardContent className="pt-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                {/* Product Name / Title */}
                <h3 className="font-semibold text-[var(--color-charcoal)]">
                  {request.productName}
                </h3>

                {/* Date */}
                <p className="text-sm text-[var(--color-charcoal)]/60 mt-1">
                  {formatDate(request.createdAt)}
                </p>
              </div>

              {/* Info icon - only show if there are parameters */}
              {hasParameters && (
                <button
                  onClick={handleInfoClick}
                  className={cn(
                    'p-1.5 rounded-full transition-colors flex-shrink-0 cursor-pointer',
                    'hover:bg-[var(--color-charcoal)]/10',
                    showOverlay && 'bg-[var(--color-charcoal)]/10'
                  )}
                  aria-label={t('requestCard.showParameters')}
                  aria-expanded={showOverlay}
                >
                  <Info className="w-4 h-4 text-[var(--color-charcoal)]/60" />
                </button>
              )}
            </div>

            {/* Status badge */}
            <div className="mt-4 flex items-center gap-2">
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                  statusConfig.className
                )}
              >
                <StatusIcon
                  className={cn('w-3.5 h-3.5', statusConfig.animate && 'animate-spin')}
                />
                <span>{statusConfig.label}</span>
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </Link>
    </div>
  );
}
